import React, { useState } from 'react';
import { makeStyles } from '@mui/styles';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Divider from '@mui/material/Divider';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import '../fichierCss/Evenements.css'
import { useNavigate } from 'react-router-dom';
import Ressource from '../modeles/Ressource';
import { obtenirUrlImage } from '../utils/ressourceImageUtils';

const useStyles = makeStyles({
  root: {
    width: '100%',
    backgroundColor: '#fff',
  },
  resumeClamp: {
    lineHeight: 1.4,
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  voirPlus: {
    cursor: 'pointer',
    color: '#0f766e',
    fontWeight: 600,
    fontSize: '0.82rem',
    userSelect: 'none',
    textDecoration: 'underline',
  },
});

interface ListeEvenementsProps {
  handleListItemClick?: (event: React.MouseEvent, index: number) => void;
  selectedIndex?: number;
  onEvenementClick?: (evenement: Ressource) => void;
  evenements?: Ressource[];
  isLoading?: boolean;
  hasError?: boolean;
}

const ListeEvenements = ({
  handleListItemClick,
  selectedIndex,
  onEvenementClick,
  evenements = [],
  isLoading = false,
  hasError = false,
}: ListeEvenementsProps) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const [selectedInterne, setSelectedInterne] = useState(0);
  const indexSelectionne = typeof selectedIndex === 'number' ? selectedIndex : selectedInterne;

  const ouvrirContenuEvenement = (evenement: Ressource) => {
    if (onEvenementClick) {
      onEvenementClick(evenement);
      return;
    }

    const imageUrl = obtenirUrlImage(evenement);
    navigate('/contenu-generique', {
      state: {
        contenu: {
          type: 'Evenement',
          id: evenement.id,
          titre: evenement.titre,
          description: evenement.description || evenement.resume || '',
          image: imageUrl,
        },
      },
    });
  };

  return (
    <>
      <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a2740', px: 1, pt: 1, pb: 0.5 }}>
        Evenements a venir
      </Typography>
      <List sx={{ width: '100%', bgcolor: 'background.paper', pt: 0 }}>
        {evenements.length === 0 && (
          <ListItem>
            <ListItemText primary="Aucun evenement disponible." />
          </ListItem>
        )}
        {evenements.map((evenement, index) => {
          const imageUrl = obtenirUrlImage(evenement);
          const texteComplet = `${evenement.resume || ''} ${evenement.description || ''}`.trim();
          const tronque = texteComplet.length > 100;
          const texte = tronque ? texteComplet.slice(0, 100) : texteComplet;

          return (
            <React.Fragment key={evenement.id || index}>
              <ListItem
                alignItems="flex-start"
                sx={{
                  cursor: 'pointer',
                  borderRadius: 2,
                  px: 1,
                  py: 1,
                  ...(indexSelectionne === index && {
                    background: 'rgba(191,219,254,0.35)',
                    border: '1px solid rgba(15,118,110,0.3)',
                  }),
                }}
                onClick={(event) => {
                  setSelectedInterne(index);
                  if (handleListItemClick) handleListItemClick(event, index);
                  ouvrirContenuEvenement(evenement);
                }}
              >
                <Box
                  sx={{
                    width: 110,
                    height: 75,
                    mr: 1.5,
                    flexShrink: 0,
                    borderRadius: 1,
                    overflow: 'hidden',
                    bgcolor: '#e8f0f7',
                  }}
                >
                  <img
                    src={imageUrl}
                    alt={evenement.titre}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: imageUrl ? 'block' : 'none' }}
                    onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                  />
                </Box>
                <ListItemText
                  primary={evenement.titre}
                  primaryTypographyProps={{ fontWeight: 700, fontSize: '0.92rem', color: '#1a2740' }}
                  secondary={
                    <Typography
                      component="span"
                      variant="body2"
                      sx={{ color: 'text.secondary', display: 'block' }}
                      className={classes.resumeClamp}
                    >
                      {texte}
                      {tronque && (
                        <span
                          className={classes.voirPlus}
                          onClick={(e) => { e.stopPropagation(); ouvrirContenuEvenement(evenement); }}
                        >
                          {'... voir plus'}
                        </span>
                      )}
                    </Typography>
                  }
                />
              </ListItem>
              {index < evenements.length - 1 && (
                <Divider component="li" sx={{ mx: 1 }} />
              )}
            </React.Fragment>
          );
        })}
      </List>
    </>
  );
};

export default ListeEvenements;
